import { IObservableValue } from "mobx";
import { Observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";
import { CvpSettings } from "../api/settings/CvpSettings";
import Layout from "../Modules/Layout";
import { ThemeProvider } from "../Themes/generated";
import { AppType } from "../WSUtils/AppType";
import { PluginComponentRenderer } from "../WSUtils/PluginComponents";
import { UserInfoContextProvider } from "../WSUtils/UserInfo";
import { UserInfoContext } from "../WSUtils/UserInfoContext";
import { WSRoute } from "../WSUtils/WSRouter";
import { CVPQueryClientProvider } from "../WSUtils/queryClient";
import { usePrimeLocale } from "../WSUtils/usePrimeLocale";
import { concatClassnames, guid } from "../WSUtils/util";
import { TranslationProvider, useLocStore } from "../wsLocalization";
import { LocContext } from "../wsLocalization/LocContext";
import { AppGlobalStyles, UserProvidedStyle } from "./AppGlobalStyles";
import MessagesPage from "./Pages/Messages";
import { AppStyleProps, getThemeSettingValue } from "./getThemeSettingValue";
import { ProfilePage } from "./Pages/Profile";
import _ from "lodash";

export interface CVPortalProps {
  settings: IObservableValue<CvpSettings>;
}

export const App = ({ settings }: CVPortalProps) => {
  return (
    <React.Suspense fallback={null}>
      <UserInfoContextProvider settings={settings}>
        <CVPQueryClientProvider>
          <AppContainerInner />
          <TranslationProvider />
        </CVPQueryClientProvider>
      </UserInfoContextProvider>
    </React.Suspense>
  );
};

const AppContainerInner = () => {
  const userInfo = React.useContext(UserInfoContext);
  usePrimeLocale();

  // prefetch
  useLocStore(LocContext.GENERAL);

  return (
    <Observer
      render={() => {
        // apply user provided stylesheets
        React.useEffect(() => {
          const urls = (userInfo.settings.cssUrl ?? "")
            .split("\n")
            .map((url) => url.trim())
            .filter((url) => !!url);

          document
            .querySelectorAll(`link[id^="cvp-user-css-"]`)
            .forEach((link) => {
              if (!urls.includes(link.getAttribute("href"))) {
                console.log("removing", link);
                link.remove();
              }
            });

          urls.forEach((url) => {
            if (document.head.querySelector(`link[href="${url}"]`)) {
              return;
            }

            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = url;

            link.id = `cvp-user-css-${guid()}`;

            document.head.appendChild(link);
            console.log("appended", link);
          });
        }, [userInfo.settings.cssUrl]);

        return (
          <ThemeProvider
            theme={userInfo.settings.theme}
            replaceColors={
              // use original stylesheet if primaryColor is unchanged
              userInfo.settings.primaryColor !== "var(--primary-color)"
            }
          >
            <AppGlobalStyles />
            <UserProvidedStyle css={userInfo.settings.css} />

            <AppStyles
              className={concatClassnames([
                "p-component",
                "cvp-app-container",
                userInfo.appType === AppType.iframe &&
                  "cvp-app-container-iframe",
                userInfo.appType === AppType.script &&
                  "cvp-app-container-script",
                userInfo.appType === AppType.cvWebtempNo &&
                  "cvp-app-container-cvwebtempno",
              ])}
              fontSize={userInfo.settings.fontSize}
              fontFamily={userInfo.settings.fontFamily}
              backgroundColor={userInfo.settings.backgroundColor}
              primaryColor={userInfo.settings.primaryColor}
              themeName={userInfo.settings.theme}
              cssUrl={userInfo.settings.cssUrl}
              appType={userInfo.appType}
            >
              <Layout>
                <PluginComponentRenderer />
                <WSRoute
                  path={/^\/?frontpage$/i}
                  render={() => <FrontPage />}
                />
                <WSRoute
                  path={/^\/?messages$/i}
                  render={() => <MessagesPage />}
                />
                <WSRoute
                  path={/^\/?workschedule$/i}
                  render={() => <WorkSchedule />}
                />
                <WSRoute
                  path={/^\/?questionnaire$/i}
                  render={() => <Questionnaire />}
                />
                <WSRoute
                  path={/^\/?profile$/i}
                  render={() => <ProfilePage />}
                />

                <WSRoute path={/^\/?jobs$/i} render={() => <Jobs />} />
                <WSRoute path={/^\/?job$/i} render={() => <Job />} />
                <WSRoute path={/^\/?(cv|apply)$/i} render={() => <CVEdit />} />
                <WSRoute
                  path={/^\/?(colortest)$/i}
                  render={() => <ColorTest />}
                />
                <WSRoute
                  path={[/^\/?admin$/i, /^\/?admin\/.*/i]}
                  render={() => <AdminSettingsEditor />}
                />
                <WSRoute
                  path={[/^\/?login$/i]}
                  render={() => {
                    window.location.href = `auth/visma-connect/login`;
                  }}
                />
                <WSRoute
                  path={[/^\/?logout$/i]}
                  render={() => {
                    window.location.href = `auth/logout`;
                  }}
                />
              </Layout>
            </AppStyles>
          </ThemeProvider>
        );
      }}
    />
  );
};

const FrontPage = React.lazy(() => import("./Pages/FrontPage"));
const ColorTest = React.lazy(() => import("./Pages/ColorTest"));
const WorkSchedule = React.lazy(() => import("./Pages/WorkSchedule"));
const Jobs = React.lazy(() => import("./Pages/Jobs"));
const Questionnaire = React.lazy(() => import("./Pages/Questionnaire"));
const Job = React.lazy(() => import("./Pages/Job"));
const CVEdit = React.lazy(() => import("./Pages/CVEdit"));
const AdminSettingsEditor = React.lazy(
  () => import("./Pages/AdminSettingsEditor")
);

const AppStyles = styled.div<AppStyleProps>`
  &.cvp-app-container {
    color: var(--text-color);
    box-sizing: border-box;

    &.cvp-app-container-cvwebtempno {
      min-height: 100vh;
    }
    &:not(.cvp-app-container-cvwebtempno) {
      width: calc(min(var(--standard-cvp-width), 100%) - 2rem);
      max-width: calc(min(var(--standard-cvp-width), 100%) - 2rem);
      margin-left: auto;
      margin-right: auto;
      margin-top: 0;
      margin-bottom: 0;
      padding-top: 1rem;
      padding-bottom: 1rem;
    }

    ${(p) => getThemeSettingValue(p, "backgroundColor")};
    ${(p) => getThemeSettingValue(p, "fontSize")};

    ${(p) => getThemeSettingValue(p, "fontFamily")};

    .p-component,
    .p-inputtext {
      ${(p) => getThemeSettingValue(p, "fontFamily")};
    }

    /* Undo special css in customer pages */
    mark {
      padding: 0;
      background: yellow;
    }
  }

  .hidden {
    display: none;
  }

  .p-link {
    color: var(--text-color);
  }
`;
